import { useCallback, useEffect, useMemo } from 'react';
import {
  AccountStatus,
  CardTypesEnum,
  CoveragePeriods,
  EnrollmentStatus,
  InvestmentPathType,
  SpendPeriodType,
  useQuery,
} from '@common-fe/common-fe';
import { CIPStatus, Enrollment, onlyDateFromServer } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IEnrollmentDto } from '@/dtos';
import { useAuthStore } from '@/modules/user/stores';
import formatCurrency from '@/utils/modifiers/formatCurrency';

import { AccountStatusEnum } from '../transaction.types';

export const HSA_TYPE_KEY = 'HSA';
export const ACTIVE_ENROLLMENT_STATUS = 'ACTIVE';
const NOTIONAL_OMNIBUS = 'NOTIONAL';
export enum FUNDING_METHODS {
  STANDARD_NOTIONAL = 'STANDARD_NOTIONAL',
  PREFUND_SPENDDOWN = 'PREFUND_SPENDDOWN',
  PREFUND_AND_CONTRIBUTION = 'PREFUND_AND_CONTRIBUTION',
  CONTRIBUTION_ONLY = 'CONTRIBUTION_ONLY',
}

export const QUERY_KEY = 'getAccounts';

const useMyEnrollments = (id?: string | number | null, status?: string | null, enabled: boolean = true) => {
  const { user, auth } = useAuthStore();

  const { isLoading, isSuccess, error, data, refetch } = useQuery(
    [QUERY_KEY, status, auth, enabled],
    ({ signal }) =>
      api.get(API_URL.ENROLLMENTS, {
        signal,
        params: _.pickBy({
          status,
        }),
      }),
    {
      enabled: Boolean(user && enabled),
    }
  );

  useEffect(() => {
    if (user?.employee && auth) refetch();
  }, [user, auth, refetch]);

  const getFormatedEnrollment = useCallback(
    (rawEnrollment: IEnrollmentDto): Enrollment => ({
      id: `${rawEnrollment.account_id || ''}`,
      name: rawEnrollment.enrollment_title || '',
      available: rawEnrollment.available_amount || 0,
      election: rawEnrollment.election_amount || 0,
      CIPStatus: rawEnrollment.cip_status as unknown as CIPStatus,
      isCIPRequired: rawEnrollment.cip_required,
      isReplenished: rawEnrollment.is_replenished,
      accountType: rawEnrollment.account_type as CardTypesEnum,
      allowInvestments: rawEnrollment.allow_investments || false,
      accountStatus: rawEnrollment.account_status as unknown as AccountStatus,
      enrollmentStatus: rawEnrollment?.status as unknown as EnrollmentStatus,
      investmentThreshold: rawEnrollment.investment_threshold || 0,
      minimumInvestmentAmount: rawEnrollment.minimum_investment_amount,
      investmentOptionTypes: rawEnrollment.investment_option_types as unknown as InvestmentPathType[],
      // pendingTradeAmount: rawEnrollment.pending_trade_amount,
      // @ts-ignore
      accountNumber: rawEnrollment?.account_number,
      allowIndividualContributions: rawEnrollment.allow_individual_contributions,
      contributionMonitoringRequired: rawEnrollment?.contribution_monitoring_required,
      minimumContributionAmount: rawEnrollment?.minimum_contribution_amount,
      generateStatements: rawEnrollment?.generate_statements,
      catchUpElection: rawEnrollment?.catch_up_election,
      planCode: rawEnrollment?.plan_code,
      planId: _.toString(rawEnrollment?.plan_id),
      displayName: rawEnrollment.display_name,
      isRollover: rawEnrollment?.is_rollover,
      spendPeriodType: rawEnrollment?.spend_period_type as unknown as SpendPeriodType,
      policyId: `${rawEnrollment?.policy_id || ''}`,
      remainingRollover: Math.min(
        rawEnrollment.available_amount || 0,
        rawEnrollment?.current_rollover_remaining || rawEnrollment?.prior_rollover_remaining || 0
      ),
      remainingRolloverDate: rawEnrollment?.remaining_rollover_balance_date
        ? dayjs(rawEnrollment.remaining_rollover_balance_date).toDate()
        : undefined,

      coveragePeriods: rawEnrollment?.coverage_periods?.map((item) => ({
        startDate: item?.coverage_date_start
          ? dayjs(onlyDateFromServer(item?.coverage_date_start as unknown as string)).format(
              DEFAULT_DATE_FORMAT
            )
          : 'No deadline',
        endDate: item?.coverage_date_end
          ? dayjs(onlyDateFromServer(item?.coverage_date_end as unknown as string)).format(
              DEFAULT_DATE_FORMAT
            )
          : 'No deadline',
        limitedPeriods: item?.limited_periods?.map((limitedPeriod) => ({
          limitedPeriodStart: limitedPeriod?.limited_period_start
            ? dayjs(onlyDateFromServer(limitedPeriod?.limited_period_start as unknown as string)).format(
                DEFAULT_DATE_FORMAT
              )
            : null,
          limitedPeriodEnd: limitedPeriod?.limited_period_end
            ? dayjs(onlyDateFromServer(limitedPeriod?.limited_period_end as unknown as string)).format(
                DEFAULT_DATE_FORMAT
              )
            : null,
        })),
      })) as unknown as CoveragePeriods[],
      coverageDateStart: rawEnrollment?.coverage_periods?.[rawEnrollment?.coverage_periods?.length - 1]
        ?.coverage_date_start
        ? dayjs(
            onlyDateFromServer(
              (rawEnrollment?.coverage_periods?.[rawEnrollment?.coverage_periods?.length - 1]
                ?.coverage_date_start as unknown as string) || ''
            )
          ).format(DEFAULT_DATE_FORMAT)
        : '',
      coverageDateEnd: rawEnrollment?.coverage_periods?.[0]?.coverage_date_end
        ? dayjs(
            onlyDateFromServer(rawEnrollment?.coverage_periods?.[0]?.coverage_date_end as unknown as string)
          ).format(DEFAULT_DATE_FORMAT)
        : '',
      submitClaimsDateEnd: rawEnrollment?.submit_claims_date_end
        ? dayjs(onlyDateFromServer(rawEnrollment.submit_claims_date_end as unknown as string)).format(
            DEFAULT_DATE_FORMAT
          )
        : '',
      deductibleMetDate: rawEnrollment?.deductible_met_date
        ? dayjs(onlyDateFromServer(rawEnrollment.deductible_met_date as unknown as string)).format(
            DEFAULT_DATE_FORMAT
          )
        : undefined,
      // @ts-ignore
      coverageTier: rawEnrollment?.coverage_tier_type,
      planValidFrom: rawEnrollment?.plan_valid_from as unknown as string,
      electionPeriods: rawEnrollment?.election_periods?.map((item) => ({
        id: `${item.id || ''}`,
        policyId: `${item.policy_id || ''}`,
        amount: item.amount || 0,
        validFrom: item.valid_from
          ? dayjs(onlyDateFromServer(item.valid_from as unknown as string)).format(DEFAULT_DATE_FORMAT)
          : 'No deadline',
        validTo: item.valid_to
          ? dayjs(onlyDateFromServer(item.valid_to as unknown as string)).format(DEFAULT_DATE_FORMAT)
          : 'No deadline',
      })),
    }),
    []
  );
  const currentMyAccount = useMemo(() => {
    const list = data?.data || [];
    if (!id) {
      return null;
    }
    const foundItem = list.find((item) => `${item.account_id}` === id);
    if (foundItem) {
      return getFormatedEnrollment(foundItem);
    }
    return null;
  }, [data, getFormatedEnrollment, id]) as unknown as Enrollment;
  // const currentMyAccount: RawEnrollment | null | undefined = useMemo(
  //   () => {
  //     const list = data?.data || [];
  //     const item = list.find((item) => `${item.account_id}` === id);
  //     if(item) {
  //       // return {
  //       //   id: item.account_id,
  //       //   name: item.enrollment_title,
  //       //   available: item.available_amount,
  //       //   election: item.election_amount,
  //       //   accountType: item.account_type,
  //       // }
  //     }
  //     return null;
  //   }), [data, id],
  // );
  const mainHSAEnrollment = useMemo(() => {
    const list = data?.data || [];
    const foundItem = list.find((item) => item.account_type === HSA_TYPE_KEY);
    if (foundItem) {
      return getFormatedEnrollment(foundItem);
    }

    return null;
  }, [data, getFormatedEnrollment]);
  const isHSAExists = useMemo(() => {
    const list = data?.data || [];
    return list.some((item) => item.account_type === HSA_TYPE_KEY);
  }, [data]);
  const isActiveHSAExists = useMemo(
    () =>
      data?.data?.some(
        (item) => item.account_type === HSA_TYPE_KEY && item.enrollment_status === ACTIVE_ENROLLMENT_STATUS
      ),
    [data]
  );
  const hasOpenHSA = useMemo(
    () =>
      data?.data?.some(
        (item) => item.account_type === HSA_TYPE_KEY && item.account_status === AccountStatusEnum.OPEN
      ),
    [data]
  );
  const isCurrentHSA = useMemo(() => {
    if (currentMyAccount) {
      return currentMyAccount.accountType === HSA_TYPE_KEY;
    }
    return false;
  }, [currentMyAccount]);

  const hasEnrollmentWithDisplayName = useMemo(() => {
    const list = data?.data || [];
    return list.some((item) => item.display_name);
  }, [data]);

  const isGenerateStatementsEnabled = useMemo(
    () => !!currentMyAccount?.generateStatements,
    [currentMyAccount]
  );

  const formattedData = useMemo(
    () => data?.data?.map((item) => getFormatedEnrollment(item)),
    [data, getFormatedEnrollment]
  ) as Enrollment[];

  const activeEnrollments = useMemo(() => {
    return formattedData?.filter(
      (enrollment) =>
        enrollment?.enrollmentStatus === EnrollmentStatus.Active ||
        enrollment?.enrollmentStatus === EnrollmentStatus.Canceled
    );
  }, [formattedData]);

  // const isActiveAndReplenished = useMemo(() => {
  //   return formatedData?.some((item) => !!item?.coveragePeriods?.length && item?.isReplenished);
  // }, [formatedData]);

  const isActiveAndReplenished = useMemo(() => {
    const isReplenished = (fundingType?: string, omnibusType?: string) => {
      if (fundingType === FUNDING_METHODS.CONTRIBUTION_ONLY && omnibusType === NOTIONAL_OMNIBUS) {
        return true;
      }
      return !!fundingType && fundingType !== FUNDING_METHODS.CONTRIBUTION_ONLY;
    };
    const currentData = data?.data || [];
    return currentData.some(
      (item) =>
        !!item?.coverage_periods?.length && isReplenished(item?.funding_method_type, item?.omnibus_type)
    );
  }, [data]);

  const breadcrumbAccountTitle = useMemo(() => {
    const accountType = currentMyAccount?.accountType || '';
    const availableAmount = formatCurrency(currentMyAccount?.available || 0);
    const planStartYear = currentMyAccount?.planValidFrom
      ? dayjs(currentMyAccount?.planValidFrom).format('YYYY')
      : '';
    return `${accountType} ${planStartYear} (${availableAmount})`;
  }, [currentMyAccount]);
  const currentOpenHSA = useMemo(() => {
    if (id) {
      if (currentMyAccount?.accountStatus) {
        if (currentMyAccount?.accountStatus === AccountStatus.OPEN) {
          return currentMyAccount;
        }
        return;
      }
      return;
    }

    const foundItem = data?.data?.find(
      (item) => item.account_type === HSA_TYPE_KEY && item.account_status === AccountStatusEnum.OPEN
    );
    if (foundItem) {
      return getFormatedEnrollment(foundItem) as unknown as Enrollment;
    }
    return;
  }, [currentMyAccount, data?.data, getFormatedEnrollment, id]);

  const isOpenHSAExist = useMemo(() => !!currentOpenHSA, [currentOpenHSA]);

  return {
    data: formattedData || [],
    isHSAExists,
    hasActiveEnrollments: activeEnrollments?.length > 0,
    isOpenHSAExist,
    currentOpenHSA,
    isActiveHSAExists,
    hasOpenHSA,
    mainHSAEnrollment,
    currentMyAccount,
    error,
    isCurrentHSA,
    hasEnrollmentWithDisplayName,
    isGenerateStatementsEnabled,
    isActiveAndReplenished,
    isLoading,
    isSuccess,
    breadcrumbAccountTitle,
  };
};

export default useMyEnrollments;
